/**
 * A page to come back to after logging in, when the way there leaves this URL:
 * signing up by e-mail ends with the verification link, often in another tab, and
 * then a plain /login. The shop-draft claim page remembers itself here.
 *
 * Login and sign-up prefer a ?redirect= and otherwise take this one (once). It
 * wins over the onboarding redirect: pages that need a finished profile still send
 * there through their onboardingGuard. Only app-internal paths, for one hour.
 */
const STORAGE_KEY = 'schenkwerk_return_url';
const MAX_AGE_MS = 60 * 60 * 1000;

interface StoredReturnUrl {
  url: string;
  savedAt: number;
}

const internal = (url: string | null | undefined): url is string =>
  !!url && url.startsWith('/') && !url.startsWith('//');

export function rememberReturnUrl(url: string): void {
  if (!internal(url)) return;
  try {
    const value: StoredReturnUrl = { url, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Not remembered: the user opens the link again after logging in
  }
}

/** The ?redirect= if given, else the remembered page (forgotten once read). */
export function takeReturnUrl(redirectParam: string | null | undefined): string | null {
  let stored: string | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    const value = raw ? (JSON.parse(raw) as Partial<StoredReturnUrl>) : null;
    if (value && typeof value.savedAt === 'number' && Date.now() - value.savedAt <= MAX_AGE_MS) {
      stored = internal(value.url) ? value.url : null;
    }
  } catch {
    // Unreadable: nothing remembered
  }
  return internal(redirectParam) ? redirectParam : stored;
}
